/**
 * Lightweight floating-element positioner. Computes coordinates to place a
 * floating element relative to an anchor with the same placement vocabulary
 * `@floating-ui/react` exposes (without taking it as a peer dep).
 *
 * Auto-flips along the main axis when the preferred placement would clip,
 * and shifts along the cross axis to stay inside the viewport.
 *
 * The full `@floating-ui/react` peer is an *optional* dep (declared in
 * package.json) for advanced needs (middleware pipelines, virtual anchors,
 * autoUpdate): most overlays in sdk-ui use this lightweight implementation
 * which is enough for tooltip/popover/menu placement.
 */

export type Placement =
  | 'top' | 'top-start' | 'top-end'
  | 'bottom' | 'bottom-start' | 'bottom-end'
  | 'left' | 'left-start' | 'left-end'
  | 'right' | 'right-start' | 'right-end';

export type Side = 'top' | 'bottom' | 'left' | 'right';
export type Align = 'start' | 'center' | 'end';

export interface PositionerOptions {
  placement?: Placement;
  /** Distance between anchor edge and floating element. */
  offset?: number;
  /** Min distance from viewport edges. */
  padding?: number;
  /** Allow flipping to the opposite side if the preferred side clips. */
  flip?: boolean;
  /** Shift along the cross axis to stay inside the viewport. */
  shift?: boolean;
}

export interface ComputedPosition {
  x: number;
  y: number;
  /** Resolved placement after flip/shift. */
  placement: Placement;
  /** Arrow position relative to the floating element. */
  arrow: { x: number; y: number; side: Side };
}

function splitPlacement(p: Placement): { side: Side; align: Align } {
  const [side, align = 'center'] = p.split('-') as [Side, Align?];
  return { side, align };
}

function combine(side: Side, align: Align): Placement {
  return (align === 'center' ? side : `${side}-${align}`) as Placement;
}

function opposite(side: Side): Side {
  return side === 'top' ? 'bottom'
    : side === 'bottom' ? 'top'
    : side === 'left' ? 'right'
    : 'left';
}

export function computePosition(
  anchor: DOMRect,
  floating: { width: number; height: number },
  options: PositionerOptions = {}
): ComputedPosition {
  const { placement = 'bottom', offset = 8, padding = 8, flip = true, shift = true } = options;
  if (typeof window === 'undefined') {
    return { x: 0, y: 0, placement, arrow: { x: 0, y: 0, side: 'top' } };
  }
  const vw = window.innerWidth, vh = window.innerHeight;
  let { side, align } = splitPlacement(placement);

  function placeFor(s: Side, a: Align) {
    let x = 0, y = 0;
    if (s === 'top') {
      y = anchor.top - floating.height - offset;
      x = align === 'start' ? anchor.left
        : align === 'end' ? anchor.right - floating.width
        : anchor.left + anchor.width / 2 - floating.width / 2;
    } else if (s === 'bottom') {
      y = anchor.bottom + offset;
      x = a === 'start' ? anchor.left
        : a === 'end' ? anchor.right - floating.width
        : anchor.left + anchor.width / 2 - floating.width / 2;
    } else if (s === 'left') {
      x = anchor.left - floating.width - offset;
      y = a === 'start' ? anchor.top
        : a === 'end' ? anchor.bottom - floating.height
        : anchor.top + anchor.height / 2 - floating.height / 2;
    } else {
      x = anchor.right + offset;
      y = a === 'start' ? anchor.top
        : a === 'end' ? anchor.bottom - floating.height
        : anchor.top + anchor.height / 2 - floating.height / 2;
    }
    return { x, y };
  }

  let { x, y } = placeFor(side, align);

  // Flip to opposite side if main-axis clipping occurs.
  if (flip) {
    const clipsTop = y < padding;
    const clipsBot = y + floating.height > vh - padding;
    const clipsL = x < padding;
    const clipsR = x + floating.width > vw - padding;
    if ((side === 'top' && clipsTop) || (side === 'bottom' && clipsBot) ||
        (side === 'left' && clipsL) || (side === 'right' && clipsR)) {
      const flipped = placeFor(opposite(side), align);
      side = opposite(side);
      x = flipped.x;
      y = flipped.y;
    }
  }

  // Shift along the cross axis to keep inside viewport.
  if (shift) {
    if (side === 'top' || side === 'bottom') {
      x = Math.min(Math.max(padding, x), vw - floating.width - padding);
    } else {
      y = Math.min(Math.max(padding, y), vh - floating.height - padding);
    }
  }

  // Arrow position (center of the anchor edge minus floating top-left).
  let arrow = { x: 0, y: 0, side: opposite(side) as Side };
  if (side === 'top' || side === 'bottom') {
    arrow.x = anchor.left + anchor.width / 2 - x;
    arrow.y = side === 'top' ? floating.height : 0;
  } else {
    arrow.y = anchor.top + anchor.height / 2 - y;
    arrow.x = side === 'left' ? floating.width : 0;
  }

  return { x, y, placement: combine(side, align), arrow };
}
